import { useRef, useState, useEffect } from 'react';
import { AlertTriangle, Cpu, ShieldX } from 'lucide-react';
import { useTr } from '../contexts/LanguageContext';

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const ProblemSection = () => {
  const t = useTr();
  const { ref, inView } = useInView();

  const cards = [
    {
      icon: AlertTriangle,
      title: t('Coordination Failure', '조정 실패'),
      desc: t(
        'Existing multi-agent frameworks collapse at scale. Without a dedicated orchestration layer, agent swarms fragment into isolated silos — unable to share context, coordinate tasks, or achieve collective intelligence.',
        '기존의 멀티에이전트 프레임워크는 대규모에서 한계에 부딪힙니다. 전용 오케스트레이션 레이어 없이는 에이전트 스웜이 고립된 사일로로 분열되어 컨텍스트를 공유하거나 작업을 조율하거나 집단 지능을 달성할 수 없습니다.'
      ),
      stat: t('73% of enterprise AI swarms fail at >100 agents', '기업 AI 스웜의 73%가 에이전트 100개 이상에서 실패'),
      color: '#FF6B6B', id: 'coordination',
    },
    {
      icon: Cpu,
      title: t('Compute Fragmentation', '컴퓨팅 파편화'),
      desc: t(
        "GPU compute is locked behind centralized giants. Swarm operators can't access distributed compute efficiently, creating bottlenecks that throttle performance and inflate costs by 10x.",
        'GPU 컴퓨팅이 중앙화된 대형 기업에 의해 독점되고 있습니다. 스웜 운영자는 분산된 컴퓨팅에 효율적으로 접근할 수 없어 성능을 제한하고 비용을 10배 이상 부풀리는 병목 현상이 발생합니다.'
      ),
      stat: t('$47B in GPU capacity sits idle globally', '전 세계적으로 470억 달러 규모의 GPU 용량이 유휴 상태'),
      color: '#30d5c8', id: 'compute',
    },
    {
      icon: ShieldX,
      title: t('Trust Deficit', '신뢰 부족'),
      desc: t(
        'There is no standard protocol for verifying agent outputs, ensuring integrity of swarm coordination, or establishing accountability in multi-agent pipelines — making enterprise adoption impossible.',
        '에이전트 출력물 검증, 스웜 조정의 무결성 보장, 멀티에이전트 파이프라인의 책임 확립을 위한 표준 프로토콜이 존재하지 않습니다. 이는 기업 도입을 불가능하게 만듭니다.'
      ),
      stat: t('0 production-grade verification protocols exist', '프로덕션 수준의 검증 프로토콜이 존재하지 않음'),
      color: '#F59E0B', id: 'trust',
    },
  ];

  return (
    <section id="problem" className="py-20 sm:py-24 md:py-32 dot-grid" style={{ position: 'relative', zIndex: 10, background: 'var(--sb-bg-3)' }} data-testid="problem-section">
      <div style={{ position: 'absolute', top: '20%', left: '-5%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(255,107,107,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-10 relative">
        <div ref={ref} className={`mb-12 md:mb-16 anim-hidden ${inView ? 'anim-visible' : ''}`}>
          <div className="section-divider" />
          <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-3">{t('The Problem', '문제점')}</p>
          <h2 className="font-outfit font-black text-white text-3xl sm:text-4xl md:text-5xl tracking-tight">
            {t('Why Current AI Infrastructure', '현재 AI 인프라가')}<br />
            <span className="gradient-text">{t('Fails at Scale', '대규모에서 실패하는 이유')}</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-xl text-xs sm:text-sm md:text-base leading-relaxed">
            {t('The next wave of AI requires swarm-scale coordination. Today\'s infrastructure wasn\'t built for it.', '차세대 AI는 스웜 규모의 조율이 필요합니다. 현재 인프라는 이를 위해 설계되지 않았습니다.')}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div key={card.id} data-testid={`problem-card-${card.id}`}
                className={`glass card-3d animated-border rounded-2xl p-6 sm:p-8 flex flex-col gap-5 anim-hidden ${inView ? 'anim-visible' : ''}`}
                style={{ transitionDelay: `${i * 0.12}s` }}>
                <div className="flex items-start gap-4">
                  <div className="relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${card.color}12`, border: `1px solid ${card.color}35` }}>
                    <Icon size={20} style={{ color: card.color }} />
                    <div className="absolute inset-0 rounded-xl" style={{ background: `radial-gradient(circle at center, ${card.color}15 0%, transparent 70%)`, animation: 'radial-pulse 3s ease-in-out infinite' }} />
                  </div>
                  <h3 className="font-outfit font-bold text-lg sm:text-xl text-white mt-1">{card.title}</h3>
                </div>
                <p className="text-white/55 text-xs sm:text-sm leading-relaxed">{card.desc}</p>
                <div className="mt-auto pt-4 border-t border-white/5">
                  <p className="text-xs font-mono" style={{ color: card.color }}>{card.stat}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
